/**
 * Scenario Skill Resource Table
 * 
 * Structured data from the 剧本.md "Skill 资源表" tables.
 * Each scenario references the skills used in its demo workflow.
 */

export type PrivacyRating = "local" | "local-optional" | "external" | "auth-required" | "security-tool"

export interface ScenarioSkill {
  name: string
  source: string
  description: string
  role: string           // 在场景中的作用
  downloads: string      // 真实下载量
  privacy: PrivacyRating
  privacyLabel: string   // e.g. "🟢 本地离线", "🔵 外部 API"
  /** Whether this skill is pre-installed locally */
  installed?: boolean
}

export interface ScenarioMeta {
  scenarioId: string
  scenarioTitle: string
  coreCapability: string
  coreValue: string
  skills: ScenarioSkill[]
}

// ── 场景一：自媒体全流程运营 ──
const SCENARIO_01_SKILLS: ScenarioSkill[] = [
  { name: "newsapi-search", source: "openclawskills.best", description: "全球新闻实时热点查询", role: "实时抓取科技热点，为选题提供数据支撑", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "google-search", source: "openclawskills.best", description: "Google CSE 网页搜索，返回结构化结果", role: "搜索竞品内容、关键词热度分析", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "web-scraper-as-a-service", source: "openclawskills.best", description: "构建可复用网页爬虫，输出清洁结构化数据", role: "深度采集竞品文章和社交媒体讨论", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部请求" },
  { name: "writing-assistant", source: "openclawskills.best", description: "根据结构化输入生成多格式长文本内容", role: "生成视频脚本、公众号文章、口播稿", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "elevenlabs-agent", source: "openclawskills.best", description: "ElevenLabs 语音 AI，30+ 语言高质量配音", role: "生成中文 AI 配音 MP3", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "image-generation", source: "openclawskills.best", description: "调用 Flux/Veo 多模型生成图片和视频", role: "生成视频封面图、公众号配图", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "pptx", source: "openclawskills.best", description: "处理 .pptx：创建、编辑、解析幻灯片", role: "自动生成讲解 PPT 配套课件", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "bird-x-twitter", source: "openclawskills.best", description: "读取、搜索、发布 X/Twitter 内容", role: "监控话题热度，辅助内容分发", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "ollama", source: "ClawHub 热门 AI 类", description: "本地运行 Qwen2.5/LLaMA，完全离线推理", role: "本地审核脚本内容，避免敏感词", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 完全本地", installed: true },
  { name: "fast-io", source: "ClawHub 热门存储", description: "持久文件系统 + RAG 搜索，50GB 本地存储", role: "本地缓存素材库，加速后续调用", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地存储", installed: true },
  { name: "markitdown", source: "sundial-org/awesome-openclaw", description: "将 PDF/Word/PPT/Excel 转 Markdown", role: "解析参考文档，提取结构化素材", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地离线", installed: true },
]

// ── 场景二：飞书 × Office 协同 ──
const SCENARIO_02_SKILLS: ScenarioSkill[] = [
  { name: "aliyun-asr", source: "VoltAgent/awesome-openclaw", description: "阿里云 ASR，多说话人识别", role: "会议录音转文字，区分发言人", downloads: "社区热门", privacy: "local-optional", privacyLabel: "🟢 本地可选", installed: true },
  { name: "whisper", source: "sundial-org/awesome-openclaw", description: "OpenAI Whisper 本地离线语音转文字", role: "高质量语音识别，本地备选方案", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地离线", installed: true },
  { name: "feishu-common", source: "VoltAgent/awesome-openclaw", description: "飞书 API 助手，认证 + 全功能操作", role: "飞书文档创建、待办推送、多维表格", downloads: "社区热门", privacy: "auth-required", privacyLabel: "🔵 需飞书授权" },
  { name: "feishu-file-sender", source: "VoltAgent/awesome-openclaw", description: "发送 Excel/Word/PPT/PDF 到飞书", role: "将生成材料自动发送飞书群组", downloads: "社区热门", privacy: "auth-required", privacyLabel: "🔵 需飞书授权" },
  { name: "pptx", source: "openclawskills.best", description: "处理 .pptx：创建、编辑幻灯片", role: "将会议内容自动生成汇报 PPT", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "coding-agent", source: "openclawskills.best", description: "统一运行 Codex/Claude Code 等 AI 编程代理", role: "为技术需求生成结构化开发文档", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "linear-app", source: "openclawskills.best", description: "Linear.app CLI，管理 Issue/项目/周期", role: "将需求文档拆解为 Linear 开发任务", downloads: "社区热门", privacy: "auth-required", privacyLabel: "🔵 需 Linear 授权" },
  { name: "markitdown", source: "sundial-org/awesome-openclaw", description: "将 PDF/Word/PPT/Excel 转 Markdown", role: "解析 PDF 需求文档，提取结构化内容", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地离线", installed: true },
]

// ── 场景三：电商运营 ──
const SCENARIO_03_SKILLS: ScenarioSkill[] = [
  { name: "brightdata", source: "openclawskills.best", description: "Bright Data 企业级网页抓取", role: "选品数据抓取；竞品分析；投流数据监控", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "finance", source: "openclawskills.best", description: "实时追踪股票/ETF/品类销售趋势", role: "品类大盘趋势分析，辅助选品决策", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "writing-assistant", source: "openclawskills.best", description: "生成多格式专业文本", role: "4 平台差异化商品文案生成；AI 客服回复", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "image-generation", source: "openclawskills.best", description: "Flux/Veo 多模型生成高质量图片", role: "生成全平台商品主图组合；投流素材", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "hitpaw-enhancer", source: "ClawHub / HitPaw", description: "图片 AI 超分辨率增强，最高 8× 放大", role: "详情图 4K 超清处理；主图品质提升", downloads: "ClawHub 新晋", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "adaptlypost", source: "VoltAgent/awesome-openclaw", description: "多平台内容发布调度", role: "一键同步商品信息至多平台", downloads: "社区热门", privacy: "auth-required", privacyLabel: "🔵 需平台授权" },
  { name: "on-page-seo-auditor", source: "openclawskills.best", description: "分析 HTML/CSS 页面 SEO", role: "全平台 SEO 关键词矩阵优化", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部请求" },
  { name: "b2c-marketing", source: "VoltAgent/awesome-openclaw", description: "B2C 增长 Playbook", role: "投流数据分析；ROI 优化策略", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
]

// ── 场景六：合规秘书 ──
const SCENARIO_06_SKILLS: ScenarioSkill[] = [
  { name: "markitdown", source: "sundial-org/awesome-openclaw", description: "将 PDF/Word 本地批量转 Markdown", role: "本地解析合同、薪资单、人事档案", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地离线", installed: true },
  { name: "ollama", source: "ClawHub 热门 AI 类", description: "本地运行 Qwen2.5/LLaMA，完全离线推理", role: "敏感文件 AI 分析全程在本地运行", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 完全本地", installed: true },
  { name: "fast-io", source: "ClawHub 热门存储", description: "持久文件系统 + RAG 搜索，50GB 本地存储", role: "加密存储合同库 / 人事档案", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地存储", installed: true },
  { name: "writing-assistant", source: "openclawskills.best", description: "生成多格式专业文档", role: "生成劳动合同、风险报告、入职文件包", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "feishu-file-sender", source: "VoltAgent/awesome-openclaw", description: "发送 Word/PPT/PDF 到飞书", role: "将审核后的合规材料分发至相关负责人", downloads: "社区热门", privacy: "auth-required", privacyLabel: "🔵 需飞书授权" },
  { name: "n8n-workflow", source: "ClawHub/composio", description: "n8n 跨平台自动化，支持本地私有部署", role: "合同到期预警、入职流程自动化", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 可本地部署", installed: true },
  { name: "skill-vetter", source: "ClawHub 安全工具", description: "安装前审计 Skill 安全性", role: "验证所有 Skill 无数据外泄", downloads: "~3.5K", privacy: "security-tool", privacyLabel: "🛡️ 安全工具", installed: true },
]

// ── 场景九：炒股助手 ──
const SCENARIO_09_SKILLS: ScenarioSkill[] = [
  { name: "finance", source: "openclawskills.best", description: "实时股票/ETF/指数行情，支持 A 股+美股", role: "拉取自选股实时行情，市场情绪数据", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "stock-analysis", source: "Medium/florinelchis 开源", description: "S&P500 技术扫描，yfinance+TA-Lib", role: "Williams %R 超卖扫描，MACD/RSI 技术指标", downloads: "开源方案", privacy: "external", privacyLabel: "🔵 外部数据" },
  { name: "newsapi-search", source: "openclawskills.best", description: "全球新闻实时热点", role: "抓取个股新闻，分析舆情正负面情绪", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "exa-web-search-free", source: "DataCamp Top 100", description: "免费 AI 语义搜索", role: "搜索财报、机构研报、产业链分析", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 外部 API" },
  { name: "xerolite", source: "ClawHub 金融类", description: "连接 Interactive Brokers，自然语言下单", role: "一句话下单，本地运行，交易数据不外泄", downloads: "2026/3 上线", privacy: "local", privacyLabel: "🟢 本地运行", installed: true },
  { name: "mission-control", source: "ClawHub/apiyi.com", description: "日历 + 待办 + 定时提醒聚合", role: "设置价格预警、止损提醒、盘前简报", downloads: "35K", privacy: "auth-required", privacyLabel: "🔵 需日历授权" },
  { name: "fast-io", source: "ClawHub 热门存储", description: "本地持久文件系统 + AES 加密 + RAG", role: "本地存储持仓记录、交易日志", downloads: "社区热门", privacy: "local", privacyLabel: "🟢 本地存储", installed: true },
  { name: "capability-evolver", source: "ClawHub 第一名", description: "Agent 自动进化，历史记录行为分析", role: "分析交易行为模式，识别情绪化操作", downloads: "35K", privacy: "external", privacyLabel: "🔵 LLM API" },
  { name: "writing-assistant", source: "openclawskills.best", description: "生成多格式专业分析报告", role: "生成个股分析报告、交易复盘报告", downloads: "社区热门", privacy: "external", privacyLabel: "🔵 LLM API" },
]

/**
 * Scenario ID → ScenarioMeta lookup
 * Keys match DEMO_SCENARIO_LIST ids from lib/demo-scenarios/index.ts
 */
export const SCENARIO_SKILLS_MAP: Record<string, ScenarioMeta> = {
  // default 场景：发布会方案（PPT + 飞书），复用飞书协同的 skills
  "default": {
    scenarioId: "default",
    scenarioTitle: "发布会方案",
    coreCapability: "AI 产品发布会方案 + PPT + 飞书发送",
    coreValue: "从构思到分发，一条龙完成发布会筹备",
    skills: SCENARIO_02_SKILLS,
  },
  // 场景一
  "media-a": {
    scenarioId: "media-a", scenarioTitle: "自媒体·视频全流程",
    coreCapability: "选题 · 脚本 · 配音 · 封面 · 多平台发布",
    coreValue: "一句话启动全链路，让一个人顶一个团队",
    skills: SCENARIO_01_SKILLS,
  },
  "media-b": {
    scenarioId: "media-b", scenarioTitle: "自媒体·追热点写文",
    coreCapability: "热点抓取 → 深度采集 → 文章 → 配图",
    coreValue: "追热点不再手写，AI 帮你从新闻到爆文一步到位",
    skills: SCENARIO_01_SKILLS,
  },
  // 场景二
  "feishu-a": {
    scenarioId: "feishu-a", scenarioTitle: "飞书·会议转材料",
    coreCapability: "录音 → 纪要 · 待办 · PPT · 飞书分发",
    coreValue: "会议结束后零摩擦完成全套协作材料",
    skills: SCENARIO_02_SKILLS,
  },
  "feishu-b": {
    scenarioId: "feishu-b", scenarioTitle: "飞书·PDF→任务",
    coreCapability: "PDF 需求文档 → 飞书文档 + Linear 任务",
    coreValue: "需求文档秒变结构化任务，开发无缝衔接",
    skills: SCENARIO_02_SKILLS,
  },
  // 场景三
  "ecommerce-a": {
    scenarioId: "ecommerce-a", scenarioTitle: "电商·选品上架",
    coreCapability: "选品 → 文案 → 主图 → 上架 → SEO",
    coreValue: "一人完成整个运营团队的工作量",
    skills: SCENARIO_03_SKILLS,
  },
  "ecommerce-b": {
    scenarioId: "ecommerce-b", scenarioTitle: "电商·客服投流",
    coreCapability: "AI 客服自动接单 + 投流数据分析与优化",
    coreValue: "全天候 AI 客服 + 精准投流策略",
    skills: SCENARIO_03_SKILLS,
  },
  // 场景六
  "legal-a": {
    scenarioId: "legal-a", scenarioTitle: "合规·合同扫描",
    coreCapability: "合同批量风险扫描 · 本地 AI 处理",
    coreValue: "284 页合同全本地处理，永不外泄",
    skills: SCENARIO_06_SKILLS,
  },
  "legal-b": {
    scenarioId: "legal-b", scenarioTitle: "合规·入职核查",
    coreCapability: "入职材料生成 + PIPL 合规检查 + 材料分发",
    coreValue: "入职合规全流程自动化",
    skills: SCENARIO_06_SKILLS,
  },
  // 场景九
  "stock-a": {
    scenarioId: "stock-a", scenarioTitle: "炒股·个股分析",
    coreCapability: "自选股行情 → 新闻情绪 → 技术面 → 基本面",
    coreValue: "24 小时无情绪化智能监盘",
    skills: SCENARIO_09_SKILLS,
  },
  "stock-b": {
    scenarioId: "stock-b", scenarioTitle: "炒股·超卖扫描",
    coreCapability: "S&P 500 超卖筛选 → 基本面过滤 → 策略报告",
    coreValue: "全市场超卖扫描，逢低布局有据可依",
    skills: SCENARIO_09_SKILLS,
  },
  "stock-c": {
    scenarioId: "stock-c", scenarioTitle: "炒股·一键下单",
    coreCapability: "自然语言下单 · Xerolite 本地执行",
    coreValue: "一句话下单，交易数据不外泄",
    skills: SCENARIO_09_SKILLS,
  },
  "stock-d": {
    scenarioId: "stock-d", scenarioTitle: "炒股·复盘分析",
    coreCapability: "盈亏复盘 → 行为分析 → 情绪化识别 → 改进",
    coreValue: "识别情绪化操作，持续优化交易纪律",
    skills: SCENARIO_09_SKILLS,
  },
}
